"use client";

import React, { useState, useCallback } from "react";
import { useLocale } from "@/utils/LocaleContext";

// ---------------------------------------------------------------------------
// Types — driven by Core's RequiredInformation[] contract
// ---------------------------------------------------------------------------

export interface RequiredField {
  /** Unique key from the Core (e.g. "customer_name", "shipping_street") */
  FieldId: string;
  /** Label text for the user (localized by Core or fallback) */
  Label: string;
  /** HTML input type (text, email, tel, number) */
  InputType?: string;
  /** Is this field mandatory? */
  Required?: boolean;
  /** Max length for the input */
  MaxLength?: number;
  /** Placeholder text */
  Placeholder?: string;
  /** Validation pattern (regex string) */
  Pattern?: string;
  /** autoComplete hint for the browser */
  AutoComplete?: string;
}

export interface RequiredInformationGroup {
  /** Group identifier: "Customer", "Shipping", "Billing", "Custom" */
  GroupId: string;
  /** Human-readable title for the section */
  Title: string;
  /** Icon hint (optional): "user", "truck", "building", "file" */
  Icon?: string;
  /** Fields within this group */
  Fields: RequiredField[];
}

interface RequiredInformationProps {
  /** Groups of required fields sent by the Core */
  readonly groups: RequiredInformationGroup[];
  /** Callback when all data is collected — passes flat key-value map */
  readonly onChange: (data: Record<string, string>) => void;
  /** Collected values (controlled from parent) */
  readonly values: Record<string, string>;
  /** Validation errors from parent */
  readonly errors?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Icons (inline SVG to avoid external deps)
// ---------------------------------------------------------------------------

const ICONS: Record<string, React.ReactNode> = {
  user: (
    <svg className="ri-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  truck: (
    <svg className="ri-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  ),
  building: (
    <svg className="ri-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  file: (
    <svg className="ri-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function RequiredInformation({ groups, onChange, values, errors }: RequiredInformationProps) {
  const { t } = useLocale();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // All groups start expanded
    const initial: Record<string, boolean> = {};
    groups.forEach(g => { initial[g.GroupId] = true; });
    return initial;
  });

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const handleChange = useCallback((fieldId: string, value: string) => {
    onChange({ ...values, [fieldId]: value });
  }, [values, onChange]);

  if (!groups || groups.length === 0) return null;

  return (
    <div className="ri-container" role="group" aria-label={t.requiredInfoTitle ?? "Información requerida"}>
      {groups.map((group) => (
        <fieldset key={group.GroupId} className="ri-group">
          <legend className="ri-group-header">
            <button
              type="button"
              className="ri-group-toggle"
              onClick={() => toggleGroup(group.GroupId)}
              aria-expanded={expandedGroups[group.GroupId]}
              aria-controls={`ri-fields-${group.GroupId}`}
            >
              <span className="ri-group-title-row">
                {ICONS[group.Icon ?? "file"] ?? ICONS.file}
                <span className="ri-group-title">{group.Title}</span>
              </span>
              <svg
                className={`ri-chevron ${expandedGroups[group.GroupId] ? "ri-chevron--open" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </legend>

          {expandedGroups[group.GroupId] && (
            <div
              id={`ri-fields-${group.GroupId}`}
              className="ri-fields"
              role="group"
              aria-label={group.Title}
            >
              {group.Fields.map((field) => {
                const fieldError = errors?.[field.FieldId];
                const inputId = `ri-${group.GroupId}-${field.FieldId}`;
                const errorId = fieldError ? `${inputId}-error` : undefined;

                return (
                  <div key={field.FieldId} className="ri-field">
                    <label className="payment-label" htmlFor={inputId}>
                      {field.Label}
                      {field.Required && <span className="ri-required" aria-hidden="true"> *</span>}
                    </label>
                    <input
                      id={inputId}
                      type={field.InputType || "text"}
                      inputMode={field.InputType === "tel" ? "tel" : field.InputType === "email" ? "email" : field.InputType === "number" ? "numeric" : "text"}
                      autoComplete={field.AutoComplete || "off"}
                      placeholder={field.Placeholder || ""}
                      maxLength={field.MaxLength}
                      pattern={field.Pattern}
                      required={field.Required}
                      value={values[field.FieldId] || ""}
                      onChange={(e) => handleChange(field.FieldId, e.target.value)}
                      className={`payment-input ${fieldError ? "payment-input--error" : ""}`}
                      aria-invalid={fieldError ? "true" : undefined}
                      aria-describedby={errorId}
                    />
                    {fieldError && (
                      <p id={errorId} className="payment-error" role="alert">
                        <span aria-hidden="true">⚠ </span>{fieldError}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </fieldset>
      ))}
    </div>
  );
}
